"use client";

import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Image,
} from "@chakra-ui/react";
import { supabase } from "../supabase.ts";
import { useAppTheme } from "../ThemeContext.tsx";

const PasswordReset = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define colors based on theme
  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";
  const inputBg = isDark ? "gray.600" : "gray.200";

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const dashboardIconUrl = process.env.REACT_APP_MAIN_LOGO_URL || "";

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-new-password`,
      });

      if (error) throw error;

      navigate("/password-reset-otp", {
        state: {
          email,
        },
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An error occurred during password reset",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <Image
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              maxH="60px"
              mb={4}
            />
          )}
          <Heading color={headingColor}>Reset Password</Heading>
          <Text color={textColor}>
            Enter your email address and we'll send you a verification code to
            reset your password
          </Text>
          <form onSubmit={handlePasswordReset} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <Button
                type="submit"
                colorScheme="gray"
                _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                background={isDark ? "gray.700" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                width="full"
                isLoading={loading}
              >
                Send Verification Code
              </Button>
              <Button
                variant="outline"
                width="full"
                onClick={() => navigate("/login")}
                color={textColor}
                borderColor={textColor}
              >
                Back to Login
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>
    </Box>
  );
};

export default PasswordReset;
